import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LegalPersonValidatedFormComponent } from '@app/components/legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntitiyData } from '@app/components/legal-person-validated-form/legal-person-validated-form.helper';
import { LegalEntityFormGroup } from '@app/models/form.models';
import { LegalEntityRequest } from '@app/models/project-setup.models';
import { LegalEntityDetails } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { CardComponent } from '@app/ui-components/card/card.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-legal-person',
  templateUrl: './legal-person.component.html',
  styleUrl: './legal-person.component.scss',
  imports: [
    TranslatePipe,
    CardComponent,
    LegalPersonValidatedFormComponent,
    ButtonComponent,
  ],
})
export class LegalPersonComponent {
  legalPersonForm: FormGroup<LegalEntityFormGroup>;

  cancelEdit() {
    const legalEntityId = this.store.loggedInCompanyId();
    if (!legalEntityId) {
      return;
    }
    this.loadLegalEntity(legalEntityId);
  }

  save() {
    if (this.legalPersonForm.invalid) {
      this.legalPersonForm.markAllAsTouched();
      this.toast.error(
        this.translate.instant('profile.toasts.fixValidation'),
      );
      return;
    }

    const legalEntity = this.store.loggedInCompanyId();
    if (!legalEntity) {
      return;
    }

    const formValues = this.legalPersonForm.getRawValue();

    this.legalEntityService
      .updateLegalEntity(legalEntity, {
        name: formValues.name,
        taxNumber: formValues.taxNumber,
        email: formValues.email,
        country: formValues.country,
        bankAccountNumber: formValues.bankAccountNumber,
        address: {
          zipCode: formValues.address.zipCode,
          city: formValues.address.city,
          publicSpace: formValues.address.publicSpace,
          publicSpaceType: formValues.address.publicSpaceType,
          houseNumber: formValues.address.houseNumber,
          doorOrFloor: formValues.address.doorOrFloor,
          countryCode: formValues.country,
        },
      } as LegalEntityRequest)
      .pipe(
        tap((updated) => {
          this.toast.success(
            this.translate.instant('profile.toasts.savedSuccessfully'),
          );
          this.applyEntityToForm(updated as LegalEntityDetails);
        }),
        switchMap(() =>
          this.store.refreshLoggedInCompany().pipe(
            catchError(() => EMPTY),
          ),
        ),
        catchError(() => {
          this.toast.error(
            this.translate.instant('profile.toasts.saveFailed'),
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected store = inject(AuthStore);
  protected genericStore = inject(GenericStore);

  private readonly fb = inject(FormBuilder);
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.legalPersonForm = getLegalEntitiyData(this.fb);
    effect(() => {
      const legalEntityId = this.store.loggedInCompanyId();
      if (!legalEntityId) {
        return;
      }
      this.loadLegalEntity(legalEntityId);
    });
  }

  private loadLegalEntity(legalEntityId: string): void {
    this.legalEntityService
      .getLegalEntityById(legalEntityId)
      .subscribe((entity) => {
        this.applyEntityToForm(entity as LegalEntityDetails);
      });
  }

  private applyEntityToForm(entity: LegalEntityDetails): void {
    this.legalPersonForm.patchValue({
      taxNumber: entity.taxNumber ?? '',
      name: entity.name ?? '',
      email: entity.email ?? '',
      country: entity.country ?? '',
      ownershipStructure: entity.ownershipStructure ?? null,
      contactInfo: entity.contactInfo ?? '',
      website: entity.website ?? '',
      teaorNumbers: entity.teaorNumbers ?? null,
      bankAccountNumber: entity.bankAccountNumber ?? '',
      companyTypeId: entity.companyType?.id ?? '',
      address: {
        countryCode: entity.address?.countryCode ?? '',
        zipCode: entity.address?.zipCode ?? '',
        city: entity.address?.city ?? '',
        publicSpace: entity.address?.publicSpace ?? '',
        publicSpaceType: entity.address?.publicSpaceType ?? '',
        houseNumber: entity.address?.houseNumber ?? '',
        doorOrFloor: entity.address?.doorOrFloor ?? '',
      },
    });
  }
}
